import React, { createContext, useContext, useEffect, useMemo } from 'react';
import type { UseQueryResult } from '@tanstack/react-query';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../store/authStore';
import {
  useServicesStatus,
  writeLatchedStatusPayload,
  readLatchedStatusPayload,
  pickChromeStatusPayload,
  SERVICE_STATUS_QUERY_KEY,
  SERVICE_STATUS_QUERY_POLICY,
  type ServicesStatusPayload,
} from './serviceHealthQuery';
import { deriveServiceHealth, type DerivedServiceHealth } from './serviceHealth';

type ServiceHealthContextValue = {
  query: UseQueryResult<ServicesStatusPayload | null>;
  payload: ServicesStatusPayload | null | undefined;
  health: DerivedServiceHealth;
};

const ServiceHealthContext = createContext<ServiceHealthContextValue | null>(null);

/**
 * Owns the single /cp/services/status snapshot for chrome + every route.
 * Pages must read this result — they must not refetch a second inventory
 * that can disagree with the header pill.
 */
export function ServiceHealthProvider({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuthStore();
  const queryClient = useQueryClient();
  useEffect(() => {
    queryClient.setQueryDefaults(SERVICE_STATUS_QUERY_KEY, {
      ...SERVICE_STATUS_QUERY_POLICY,
      refetchInterval: false,
    });
  }, [queryClient]);
  const query = useServicesStatus(isAuthenticated, { owner: true });
  const payload = pickChromeStatusPayload(readLatchedStatusPayload(), query.data) ?? readLatchedStatusPayload();
  useEffect(() => {
    if (query.data) writeLatchedStatusPayload(query.data);
  }, [query.data]);
  const health = useMemo(() => deriveServiceHealth(payload), [payload]);

  const value = useMemo<ServiceHealthContextValue>(() => ({
    query,
    payload,
    health,
  }), [query, payload, health]);

  return (
    <ServiceHealthContext.Provider value={value}>
      {children}
    </ServiceHealthContext.Provider>
  );
}

export function useSharedServiceHealth(): ServiceHealthContextValue {
  const ctx = useContext(ServiceHealthContext);
  if (!ctx) {
    throw new Error('useSharedServiceHealth must be used within ServiceHealthProvider');
  }
  return ctx;
}

export function useOptionalSharedServiceHealth(): ServiceHealthContextValue | null {
  return useContext(ServiceHealthContext);
}
